import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useHistory, type RouteComponentProps } from 'react-router-dom';
import { useEffect } from 'react';
import { setLoadingStatus } from '../../../redux/loading';
import { openErrorSnackbar, openSuccessSnackbar, setSnackbar } from '../../../redux/snackbar';
import { getGQLErrorMessages } from '../../../utils/graphGL/graphQLHelper';
import {
  ImportedUsedVehicleDefinition,
  UsedVehicleCardInput,
  UsedVehicleCardStateEnum,
  VehicleTypeEnum,
} from '../../../api/graphql/generated/schema';
import CardDetails from '../../../components/usedVehicleCard/CardDetails';
import {
  CreateUsedVehicleCardMutationOptions,
  useCreateUsedVehicleCardMutation,
  useGetBodiesQuery,
  useGetBrandsQuery,
  useGetColorsQuery,
  useGetCountriesOfOriginQuery,
  useGetFuelTypesQuery,
  useGetImportedVehicleQuery,
  useGetTransmissionTypesQuery,
  useGetUsedVehicleCardsQuery,
} from '../../../api/graphql/generated/hooks';
import { GeneralVehicleCard, GeneralVehicleDefinition } from '../../../utils/typings';
import { AppPageNameRoutes } from '../../../routes/paths';
import { normalize } from '../../../utils/functionHelper';
import { capitalize } from '../../../utils/utils';
import { convertVehicleState, createMapping } from './converters';

const IMPORTED_CONSTS = {
  VEHICLE_TYPE: 'osobní',
} as const;

type Props = RouteComponentProps<{ importedVehicleVin: string }>;

function ImportedVehicleDefinitionEdit({ match }: Props) {
  const {
    params: { importedVehicleVin },
  } = match;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();

  const [createUsedVehicleCardMutation, { loading: loadingCreateUsedVehicleCard }] = useCreateUsedVehicleCardMutation();
  const { loading: loadingBrands, data: gqlBrands } = useGetBrandsQuery();
  const { loading: loadingFuelTypes, data: gqlFuelTypes } = useGetFuelTypesQuery();
  const { loading: loadingCountries, data: gqlCountries } = useGetCountriesOfOriginQuery();
  const { loading: loadingBodies, data: gqlBodies } = useGetBodiesQuery();
  const { loading: loadingColors, data: gqlColors } = useGetColorsQuery();
  const { loading: loadingTransmissions, data: gqlTransmissions } = useGetTransmissionTypesQuery();
  const { data: existingVehicleCard, loading: existingCardLoading } = useGetUsedVehicleCardsQuery({
    variables: {
      filter: { vin: importedVehicleVin },
    },
  });

  const {
    loading: loadingImportedVehicleDefinition,
    data: gqlImportedVehicleDefinition,
    refetch: refetchImportedVehicle,
  } = useGetImportedVehicleQuery({
    variables: { vin: importedVehicleVin },
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (existingVehicleCard && !existingCardLoading) {
      if (existingVehicleCard.getUsedVehicleCards.data.length > 0) {
        let path = AppPageNameRoutes.VEHICLE_USED_CARD_DETAIL.pageRoute.replace(
          ':usedVehicleCardId',
          existingVehicleCard.getUsedVehicleCards.data[0].id,
        );
        history.replace(path);
      }
    }
  }, [existingCardLoading, existingVehicleCard, history]);

  const anyLoading =
    loadingBrands ||
    loadingImportedVehicleDefinition ||
    loadingCreateUsedVehicleCard ||
    existingCardLoading ||
    loadingFuelTypes ||
    loadingBodies ||
    loadingColors ||
    loadingTransmissions ||
    loadingCountries;

  useEffect(() => {
    dispatch(setLoadingStatus(anyLoading));
  }, [dispatch, anyLoading]);

  const handleReset = async () => {
    refetchImportedVehicle();
  };

  const convertDefinitionToInput = (
    gqlDefinition: Partial<ImportedUsedVehicleDefinition>,
  ): GeneralVehicleDefinition => {
    const vehicleType =
      gqlDefinition.vehicleType?.toLowerCase() === IMPORTED_CONSTS.VEHICLE_TYPE
        ? VehicleTypeEnum.Personal
        : VehicleTypeEnum.Commercial;

    const brandMapping = createMapping(gqlBrands?.getBrands || []);
    const fuelTypeMapping = createMapping(gqlFuelTypes?.getFuelTypes || []);
    const colorMapping = createMapping(gqlColors?.getColors || []);
    const transmissionMapping = createMapping(gqlTransmissions?.getTransmissionTypes || []);
    const bodyTypeMapping = createMapping(
      (gqlBodies?.getBodies || []).filter((body) => body.vehicleType.id === vehicleType),
    );
    const countryOfOriginMapping = createMapping(gqlCountries?.getCountriesOfOrigin || []);

    const vehicleDefinition: GeneralVehicleDefinition = {
      ...gqlDefinition,
      line: gqlDefinition.line || gqlDefinition.model || '',
      model: gqlDefinition.model || gqlDefinition.line || '',
      vehicleState: convertVehicleState(gqlDefinition.vehicleState || ''),
      brand: {
        id: gqlDefinition.brandId ? brandMapping[normalize(gqlDefinition.brandId)] : undefined,
        label: gqlDefinition.brandId,
      },
      vehicleType,
      color: {
        color: colorMapping[normalize(gqlDefinition.color || '')] || '',
        name: capitalize(gqlDefinition.color || '') || '',
        colorType: '',
      },
      equipment: undefined,
      body: gqlDefinition.body ? bodyTypeMapping[normalize(gqlDefinition.body)] : undefined,
      fuelType: gqlDefinition.fuelType ? fuelTypeMapping[normalize(gqlDefinition.fuelType)] : undefined,
      transmissionType: gqlDefinition.transmissionType
        ? transmissionMapping[normalize(gqlDefinition.transmissionType)]
        : undefined,
      wheelbase: gqlDefinition.wheelbase,
      fourDriveWheel: gqlDefinition.fourDriveWheel ?? gqlDefinition.driveWheel === '4x4',
      countryOfOrigin: gqlDefinition.countryOfOrigin
        ? countryOfOriginMapping[normalize(gqlDefinition.countryOfOrigin)]
        : undefined,
      price: Number(gqlDefinition.price),
      stkUntil: gqlDefinition.stkUntil ? new Date(gqlDefinition.stkUntil) : undefined,
      emissionUntil: gqlDefinition.emissionUntil ? new Date(gqlDefinition.emissionUntil) : undefined,
      intoOperation: gqlDefinition.intoOperation ? new Date(gqlDefinition.intoOperation) : undefined,
    };
    return vehicleDefinition;
  };

  const handleCreateCard = (usedVehicleCardInput: UsedVehicleCardInput) => {
    let inputOptions: CreateUsedVehicleCardMutationOptions = {
      variables: {
        input: usedVehicleCardInput,
      },
    };

    createUsedVehicleCardMutation(inputOptions)
      .then((response) => {
        if (response?.data?.createUsedVehicleCard?.id) {
          dispatch(openSuccessSnackbar(t('userMessages.vehicleCard.created')));
          let path = AppPageNameRoutes.VEHICLE_USED_CARD_DETAIL.pageRoute.replace(
            ':usedVehicleCardId',
            response?.data?.createUsedVehicleCard?.id,
          );
          history.push(path);
        } else if (response.errors) {
          response.errors.forEach((error) => dispatch(openErrorSnackbar(getGQLErrorMessages(error))));
        }
      })
      .catch((error) => dispatch(setSnackbar(true, 'error', error.message)));
  };

  return (
    <>
      {!anyLoading && gqlImportedVehicleDefinition?.getImportedVehicleDefinition && (
        <CardDetails
          parentBreadcrumb={AppPageNameRoutes.IMPORTED_VEHICLE_DEFINITIONS}
          usedVehicleCard={
            {
              state: UsedVehicleCardStateEnum.InProgress,
              vehicleDefinition: gqlImportedVehicleDefinition.getImportedVehicleDefinition
                ? convertDefinitionToInput(
                    gqlImportedVehicleDefinition.getImportedVehicleDefinition as Partial<ImportedUsedVehicleDefinition>,
                  )
                : null,
              extraEquipment: (
                (gqlImportedVehicleDefinition?.getImportedVehicleDefinition?.equipment as Array<string> | undefined) ||
                []
              )?.map((equipmentName) => ({
                name: capitalize(equipmentName),
                price: 0,
              })),
              finance: undefined,
              images:
                gqlImportedVehicleDefinition?.getImportedVehicleDefinition?.imageUrls?.map((url, index) => ({
                  id: url,
                  order: index,
                  mainImage: index === 0,
                  fileName: url.split('/').pop() || `image-${index}`,
                  publicUrl: url,
                })) || [],
              seller: gqlImportedVehicleDefinition?.getImportedVehicleDefinition?.seller,
              marketing: [],
            } satisfies GeneralVehicleCard
          }
          handleCreateCard={handleCreateCard}
          refetch={handleReset}
        />
      )}
    </>
  );
}

export default ImportedVehicleDefinitionEdit;
